import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import i18n from "../i18n";
import {
  MINUTES_PER_DAY, dayKey, formatTime, formatTimeCompact, hourLabel, isSameDay,
  layoutOverlaps, minutesOfDay, snapMinutes,
} from "../utils/calendarDates";
import { CELL_HOVER, WEEKEND_TINT, typeStyle } from "../utils/calendarEvents";

const HOUR_PX = 48;                 // 1h row -> 24px per half-hour slot
const DAY_PX = HOUR_PX * 24;
const GUTTER = "w-14 sm:w-16";
const SCROLL_TO_HOUR = 7;           // first paint lands on the working day
const NOW_TICK_MS = 60000;

/**
 * Week / 3-day / day view: one shared hour grid, since the three differ only
 * in how many day columns they render.
 *
 * Untimed items (an activity with a date but no clock time -- which is most of
 * them) go in the pinned all-day row rather than on the 00:00 line, where
 * nobody scrolls and where they would collide with each other in a column
 * that has no duration to speak of.
 */
export default function CalendarTimeGrid({
  days, eventsByDay, today,
  onOpenDay, onCreateAt, onOpenEvent,
  canDrag, dragging, onDragStart, onDragEnd, onDropAt,
  writable,
}) {
  const { t } = useTranslation();
  const scroller = useRef(null);
  const didScroll = useRef(false);
  const [now, setNow] = useState(() => new Date());
  const [dropHint, setDropHint] = useState(null); // {key, minutes} | {key, allDay:true}

  useEffect(() => {
    const id = setInterval(() => setNow(new Date()), NOW_TICK_MS);
    return () => clearInterval(id);
  }, []);

  // Scroll to the working day once, then leave the offset alone: re-scrolling
  // on every date change would throw away where the user had scrolled to.
  useLayoutEffect(() => {
    if (didScroll.current || !scroller.current) return;
    const target = days.some((d) => isSameDay(d, now))
      ? Math.max(0, minutesOfDay(now) - 90)
      : SCROLL_TO_HOUR * 60;
    scroller.current.scrollTop = (target / MINUTES_PER_DAY) * DAY_PX;
    didScroll.current = true;
  }, [days, now]);

  const hours = useMemo(() => Array.from({ length: 24 }, (_, h) => h), []);
  const cols = days.length;
  const allDayByDay = useMemo(() => {
    const map = new Map();
    for (const day of days) {
      const key = dayKey(day);
      map.set(key, (eventsByDay.get(key) || []).filter((e) => e.allDay));
    }
    return map;
  }, [days, eventsByDay]);
  const maxAllDay = Math.max(0, ...[...allDayByDay.values()].map((l) => l.length));

  const timedByDay = useMemo(() => {
    const map = new Map();
    for (const day of days) {
      const key = dayKey(day);
      const timed = (eventsByDay.get(key) || []).filter((e) => !e.allDay);
      map.set(key, layoutOverlaps(timed));
    }
    return map;
  }, [days, eventsByDay]);

  const minutesFromPointer = (e, el) => {
    const rect = el.getBoundingClientRect();
    return snapMinutes((e.clientY - rect.top) / rect.height);
  };

  return (
    <div className="border border-border rounded-sm overflow-hidden bg-bg" data-testid="calendar-grid">
      <div
        ref={scroller}
        className="overflow-auto h-[62vh] min-h-[24rem] lg:h-[calc(100vh-19rem)] overscroll-contain"
      >
        {/* min-width keeps a 7-day week legible on a phone: the grid scrolls
            sideways instead of squeezing columns to a few characters. */}
        <div style={{ minWidth: cols > 1 ? cols * 104 + 64 : undefined }}>
          {/* Header and all-day row are pinned together in one sticky block:
              the all-day row has to stay visible once the body is scrolled
              (that's the whole point of pinning it), and sharing the header's
              sticky container means not hardcoding the header's height as a
              `top` offset that a font change would silently break. */}
          <div className="sticky top-0 z-30 bg-bg">
          {/* ---------- day header ---------- */}
          <div className="flex bg-bg border-b border-border">
            <div className={`${GUTTER} shrink-0 sticky left-0 z-10 bg-bg border-r border-border`} />
            {days.map((day) => {
              const isToday = isSameDay(day, today);
              const weekend = day.getDay() === 0 || day.getDay() === 6;
              return (
                <button
                  key={dayKey(day)}
                  type="button"
                  onClick={() => onOpenDay(day)}
                  className={`flex-1 min-w-0 px-2 py-2 flex items-baseline justify-center gap-1.5 border-r border-border last:border-r-0 outline-none
                    transition-colors duration-150 hover:bg-surface focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-primary/60
                    ${weekend && !isToday ? WEEKEND_TINT : ""}`}
                >
                  <span className={`text-[11px] uppercase tracking-[0.1em] capitalize ${isToday ? "text-primary font-semibold" : "text-muted"}`}>
                    {day.toLocaleDateString(i18n.language, { weekday: "short" })}
                  </span>
                  <span className={`text-sm tabular-nums font-display font-bold ${
                    isToday ? "bg-primary text-white rounded-sm px-1.5" : ""
                  }`}>
                    {day.getDate()}
                  </span>
                </button>
              );
            })}
          </div>

          {/* ---------- all-day row ---------- */}
          {/* Capped and scrollable: a day with a dozen dated tasks would
              otherwise push the hour grid off the screen entirely. */}
          <div className="flex border-b border-border bg-surface max-h-28 overflow-y-auto">
            <div className={`${GUTTER} shrink-0 sticky left-0 z-20 bg-bg border-r border-border flex items-start justify-end pr-2 pt-1.5`}>
              <span className="text-[10px] uppercase tracking-[0.1em] text-muted leading-none">
                {t("calendar.allDay")}
              </span>
            </div>
            {days.map((day) => {
              const key = dayKey(day);
              const list = allDayByDay.get(key) || [];
              const isHint = dropHint?.key === key && dropHint.allDay;
              return (
                // Pointer-only create shortcut, same reasoning as the month
                // grid's cells: keyboard users reach it via the toolbar.
                <div
                  key={key}
                  onClick={() => writable && onCreateAt(day, null)}
                  onDragOver={canDrag && dragging ? (e) => { e.preventDefault(); e.dataTransfer.dropEffect = "move"; setDropHint({ key, allDay: true }); } : undefined}
                  onDragLeave={canDrag ? () => setDropHint((p) => (p?.key === key && p.allDay ? null : p)) : undefined}
                  onDrop={canDrag && dragging ? (e) => { e.preventDefault(); setDropHint(null); onDropAt(day, null); } : undefined}
                  style={{ minHeight: Math.max(1, maxAllDay) * 24 + 10 }}
                  className={`flex-1 min-w-0 border-r border-border last:border-r-0 p-1 space-y-1 transition-colors duration-150
                    ${isHint ? "bg-primary/10 ring-1 ring-inset ring-primary/50" : ""} ${writable ? `cursor-pointer ${CELL_HOVER}` : ""}`}
                >
                  {list.map((ev) => (
                    <EventChip
                      key={ev.id} event={ev} canDrag={canDrag}
                      onDragStart={onDragStart} onDragEnd={onDragEnd} onOpenEvent={onOpenEvent}
                    />
                  ))}
                </div>
              );
            })}
          </div>
          </div>

          {/* ---------- hour body ---------- */}
          <div className="flex">
            <div className={`${GUTTER} shrink-0 sticky left-0 z-20 bg-bg border-r border-border relative`} style={{ height: DAY_PX }}>
              {hours.map((h) => (
                <div key={h} className="absolute right-2 -translate-y-1/2 text-[10px] tabular-nums text-muted"
                     style={{ top: (h / 24) * 100 + "%" }}>
                  {h === 0 ? "" : hourLabel(h, i18n.language)}
                </div>
              ))}
            </div>

            {days.map((day) => {
              const key = dayKey(day);
              const laid = timedByDay.get(key) || [];
              const isToday = isSameDay(day, today);
              const weekend = day.getDay() === 0 || day.getDay() === 6;
              const hint = dropHint?.key === key && !dropHint.allDay ? dropHint.minutes : null;
              return (
                // Click-to-create at the pointer's minute. Not keyboard-driven
                // for the same reason as above -- 48 half-hour slots per day
                // column is not a tab order anybody wants to walk.
                <div
                  key={key}
                  data-testid={`cal-col-${key}`}
                  onClick={writable ? (e) => onCreateAt(day, minutesFromPointer(e, e.currentTarget)) : undefined}
                  onDragOver={canDrag && dragging ? (e) => {
                    e.preventDefault();
                    e.dataTransfer.dropEffect = "move";
                    setDropHint({ key, minutes: minutesFromPointer(e, e.currentTarget) });
                  } : undefined}
                  onDragLeave={canDrag ? () => setDropHint((p) => (p?.key === key && !p.allDay ? null : p)) : undefined}
                  onDrop={canDrag && dragging ? (e) => {
                    e.preventDefault();
                    const minutes = minutesFromPointer(e, e.currentTarget);
                    setDropHint(null);
                    onDropAt(day, minutes);
                  } : undefined}
                  style={{ height: DAY_PX }}
                  className={`flex-1 min-w-0 relative border-r border-border last:border-r-0
                    ${weekend ? WEEKEND_TINT : ""} ${writable ? "cursor-cell" : ""}`}
                >
                  {hours.map((h) => (
                    <div
                      key={h}
                      aria-hidden="true"
                      className={`absolute inset-x-0 border-t ${h % 24 === 0 ? "border-transparent" : "border-border"}`}
                      style={{ top: (h / 24) * 100 + "%" }}
                    />
                  ))}

                  {laid.map((ev) => (
                    <TimedEvent
                      key={ev.id} event={ev} canDrag={canDrag}
                      onDragStart={onDragStart} onDragEnd={onDragEnd} onOpenEvent={onOpenEvent}
                    />
                  ))}

                  {hint != null && (
                    <div aria-hidden="true" className="absolute inset-x-0 z-[15] pointer-events-none"
                         style={{ top: (hint / MINUTES_PER_DAY) * 100 + "%" }}>
                      <div className="h-0.5 bg-primary" />
                      <span className="absolute left-1 top-1 text-[10px] tabular-nums font-medium text-white bg-primary rounded-sm px-1 py-px">
                        {formatTime(new Date(2024, 0, 1, 0, hint), i18n.language)}
                      </span>
                    </div>
                  )}

                  {isToday && <NowLine now={now} />}
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}

function NowLine({ now }) {
  const top = (minutesOfDay(now) / MINUTES_PER_DAY) * 100;
  return (
    <div
      aria-hidden="true"
      data-testid="cal-now-line"
      className="absolute inset-x-0 z-[15] pointer-events-none"
      style={{ top: top + "%" }}
    >
      <div className="relative h-0.5 bg-danger">
        <span className="absolute -left-1 -top-[2.5px] w-[7px] h-[7px] rounded-full bg-danger" />
      </div>
    </div>
  );
}

function TimedEvent({ event, canDrag, onDragStart, onDragEnd, onOpenEvent }) {
  const style = typeStyle(event.type);
  const { start, end } = event;
  const startMin = minutesOfDay(start);
  // A block that runs past midnight is capped here rather than drawn on the
  // next day: the event belongs to one day in this model, and a negative
  // height would silently disappear.
  const endMin = event.endsNextDay ? MINUTES_PER_DAY : minutesOfDay(end);
  const duration = Math.max(15, endMin - startMin);
  const width = 100 / event.lanes;

  // flex-col + justify-start below because a <button> centres its content
  // vertically by default -- in a two-hour block that drops the title into the
  // middle of an empty box, which reads as a layout bug rather than a choice.
  return (
    <button
      type="button"
      data-testid={`cal-event-${event.id}`}
      draggable={canDrag}
      onDragStart={canDrag ? (e) => {
        e.stopPropagation();
        const rect = e.currentTarget.getBoundingClientRect();
        const grabbed = rect.height > 0 ? ((e.clientY - rect.top) / rect.height) * duration : 0;
        onDragStart(event, grabbed, e);
      } : undefined}
      onDragEnd={canDrag ? onDragEnd : undefined}
      onClick={(e) => { e.stopPropagation(); onOpenEvent(event, e.currentTarget); }}
      title={`${formatTime(start, i18n.language)} – ${formatTime(end, i18n.language)} · ${event.subject}`}
      style={{
        top: (startMin / MINUTES_PER_DAY) * 100 + "%",
        height: (duration / MINUTES_PER_DAY) * 100 + "%",
        left: `calc(${event.lane * width}% + 2px)`,
        width: `calc(${width}% - 4px)`,
        minHeight: 20,
      }}
      className={`absolute z-10 flex flex-col justify-start items-stretch overflow-hidden
        rounded-sm border px-1.5 py-0.5 text-left outline-none
        transition-[background-color,box-shadow,opacity] duration-150 focus-visible:ring-2 focus-visible:z-20
        ${style.tint} ${style.hover} ${style.ring} ${event.completed ? "opacity-55" : ""}
        ${canDrag ? "cursor-grab active:cursor-grabbing" : ""}`}
    >
      <span className={`block text-[11px] font-medium leading-tight truncate ${event.completed ? "line-through" : ""}`}>
        {event.subject}
      </span>
      {/* Dropped in a 3-way overlap: ~45px of column can't hold a time range,
          and a clipped half-line of text looks broken rather than dense. */}
      {duration >= 45 && event.lanes <= 2 && (
        <span className="block text-[10px] tabular-nums opacity-80 leading-tight truncate">
          {formatTimeCompact(start, i18n.language)}–{formatTimeCompact(end, i18n.language)}
        </span>
      )}
    </button>
  );
}

function EventChip({ event, canDrag, onDragStart, onDragEnd, onOpenEvent }) {
  const style = typeStyle(event.type);
  return (
    <button
      type="button"
      data-testid={`cal-event-${event.id}`}
      draggable={canDrag}
      onDragStart={canDrag ? (e) => { e.stopPropagation(); onDragStart(event, 0, e); } : undefined}
      onDragEnd={canDrag ? onDragEnd : undefined}
      onClick={(e) => { e.stopPropagation(); onOpenEvent(event, e.currentTarget); }}
      title={event.subject}
      className={`w-full flex items-center gap-1.5 rounded-sm border px-1.5 py-0.5 text-[11px] leading-tight text-left outline-none
        transition-[background-color,opacity] duration-150 focus-visible:ring-2 ${style.tint} ${style.hover} ${style.ring}
        ${event.completed ? "opacity-55" : ""} ${canDrag ? "cursor-grab active:cursor-grabbing" : ""}`}
    >
      <span className={`w-1.5 h-1.5 rounded-full shrink-0 ${style.dot}`} />
      <span className={`truncate ${event.completed ? "line-through" : ""}`}>{event.subject}</span>
    </button>
  );
}
